import Button from 'ui/mobile-ds/Button';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import SelectRadio from '@/components/shared/SelectRadio';
import { RadioTab } from 'ui/mobile-ds';
import clsx from 'clsx';
import { Layout } from '@/components/Common/Layout';
import useClaimCreateStore, {
  ClaimCreateFormSchema,
  EClaimCreateFlowSteps,
} from '@/store/claimCreateStore';
import Stepper from '@/components/Common/Stepper/Stepper';
import { format } from 'date-fns';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ECareType, EClaimType } from '@/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getNetworkHospitalConfig } from '@/queries/networkHospital.queries';
import { getApplicableBenefits } from '@/queries/memberDetails.queries';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { getDimensions } from '@/utils';
import useBreakpoints from '@/hooks/useBreakpoints';
import 'react-day-picker/style.css';
import useWindowDimensions from '@/hooks/useWindowDimensions';
import DatePicker from '@/components/Common/DatePicker/DatePicker';
import { useAppContext } from '@/context/AppContext';
import useMobileBackPress from '@/hooks/useMobileBackPress';

const Schema = ClaimCreateFormSchema.pick({
  knowsDate: true,
  tentativeDays: true,
  date: true,
  isOffline: true,
  organisationBenefitId: true,
  insuranceProviderHandle: true,
  tpaName: true,
  typeOfBenefit: true,
  dateFieldName: true,
});

type TSchema = z.infer<typeof Schema>;

const BitMoreInformation = () => {
  const formState = useClaimCreateStore((state) => state.formState);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    // formState,
  } = useForm<TSchema>({
    resolver: zodResolver(Schema),
    defaultValues: {
      knowsDate: formState.knowsDate,
      date: formState.date
        ? format(new Date(formState.date ?? ''), 'yyyy-MM-dd')
        : '',
      tentativeDays: formState.tentativeDays,
    },
  });
  const watchKnowsDate = watch('knowsDate');
  const watchDate = watch('date');
  const watchTentativeDate = watch('tentativeDays');
  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);
  const setFormState = useClaimCreateStore((state) => state.setFormState);

  const { isWebView } = useAppContext();

  useMobileBackPress({
    onBackPress: () => {
      setCurrentStep(EClaimCreateFlowSteps.COLLECT_BASIC_INFO);
    },
    isWebView,
  });

  const currentFeatureCategory = useClaimCreateStore(
    (state) => state.currentFeatureCategory,
  );

  const currentTreatmentStage =
    currentFeatureCategory?.treatmentStageOptionList?.find(
      (stage) => stage.slug === formState.treatmentStage,
    );

  const formSchema =
    currentFeatureCategory?.formSchema?.[
      currentTreatmentStage?.formSchemaField ?? 'start_date'
    ];

  const tentativeDates = currentTreatmentStage?.tentativeDaysOptionList;
  const noTentativeDatesPresent = (tentativeDates?.length ?? 0) === 0;

  const {
    refetch: refetchApplicableBenefits,
    isFetching: isFetchingApplicableBenefits,
  } = useQuery({
    ...getApplicableBenefits({
      typeOfClaim: formState.typeOfClaim as EClaimType,
      typeOfCare: formState.typeOfCare as ECareType,
      patientId: formState.patientId ?? '',
      date: watchKnowsDate ? new Date(watchDate ?? '') : new Date(),
    }),
    enabled: false,
  });

  const { mutate: fetchNHConfig, status: nhConfigStatus } = useMutation({
    ...getNetworkHospitalConfig(null, null),
  });

  const onSubmit: SubmitHandler<TSchema> = async (data) => {
    window.dev.i('create-claim onSubmit[BitMoreInformation]: ', data);
    const extraFields: Partial<TSchema> = {};

    const { data: applicableBenefits, isError: isApplicableBenefitsError } =
      await refetchApplicableBenefits();

    if (isApplicableBenefitsError) {
      toast.error('Oops! Something went wrong. Please try again later.', {
        id: 'create-claim-error',
        style: {
          marginBottom: getDimensions('claims-footer').height,
        },
      });
      return;
    }

    extraFields.isOffline =
      (applicableBenefits?.length ?? 0) > 0 ? false : true;
    setValue('isOffline', extraFields.isOffline);

    extraFields.organisationBenefitId =
      applicableBenefits?.[0]?.orgBenefitId ?? null;
    extraFields.typeOfBenefit = applicableBenefits?.[0].typeOfBenefit ?? null;
    extraFields.insuranceProviderHandle =
      applicableBenefits?.[0].insuranceProviderHandle ?? null;
    extraFields.tpaName = applicableBenefits?.[0].tpaName ?? null;
    setValue('organisationBenefitId', extraFields.organisationBenefitId);
    setValue('typeOfBenefit', extraFields.typeOfBenefit);
    setValue('insuranceProviderHandle', extraFields.insuranceProviderHandle);
    setValue('tpaName', extraFields.tpaName);
    setFormState({ ...data, ...extraFields });
    Cookies.set('nh-insurer-handle', extraFields.insuranceProviderHandle ?? '');
    Cookies.set('nh-tpa', extraFields.tpaName ?? '');

    fetchNHConfig(
      {
        insurer: extraFields.insuranceProviderHandle,
        tpa: extraFields.tpaName,
      },
      {
        onSuccess: (data) => {
          if (data?.enabled) {
            setCurrentStep(EClaimCreateFlowSteps.NETWORK_HOSPITAL_V2);
          } else {
            setCurrentStep(EClaimCreateFlowSteps.NETWORK_HOSPITAL_V1);
          }
        },
        onError: (error) => {
          setCurrentStep(EClaimCreateFlowSteps.NETWORK_HOSPITAL_V1);
          window.dev.e('Error fetching network hospital config', error);
        },
      },
    );
  };

  const onError: SubmitErrorHandler<TSchema> = (errors) => {
    window.dev.e('create-claim onSubmit[BitMoreInformation]: ', errors);
    toast.error('Please fill all the mandatory fields', {
      id: 'create-claim-error',
      style: {
        marginBottom: getDimensions('claims-footer').height,
      },
    });
  };

  useEffect(() => {
    setValue('dateFieldName', formState.dateFieldName ?? '');
  }, [setValue, formState.dateFieldName]);

  const { isMobile } = useBreakpoints();
  const [[viewportWidth]] = useWindowDimensions();

  const isFetching =
    isFetchingApplicableBenefits || nhConfigStatus === 'pending';

  const dateInputValue = watchDate
    ? format(new Date(watchDate ?? ''), 'dd-MMM-yyyy')
    : '';

  return (
    <Layout.Main
      className={clsx({ '!max-w-[100vw]': isMobile })}
      headerProps={{
        onBack: () => setCurrentStep(EClaimCreateFlowSteps.COLLECT_BASIC_INFO),
        title: 'Tell us a bit more',
        description: 'We need details of your treatment dates',
        children: (
          <div className="flex justify-between md:hidden">
            <h4 className="text-sm text-mobile-hero-100 font-medium leading-5">
              Cashless Claim
            </h4>
            <span className="text-sm leading-5 text-mobile-text-hierarchy-2">
              Step 3/4
            </span>
          </div>
        ),
      }}
    >
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        className="flex flex-1 flex-col h-full pb-24"
      >
        <fieldset
          disabled={isFetching}
          className="disabled:cursor-not-allowed min-w-full"
        >
          <div className="flex flex-col h-full flex-1 gap-8 md:gap-14">
            {noTentativeDatesPresent ? null : (
              <div className="flex flex-col gap-4">
                <label
                  htmlFor="dateOfAdmission"
                  className="text-sm font-medium text-mobile-text-hierarchy leading-5 after:content-['*'] after:text-primary-500"
                >
                  Do you know your {formSchema?.text?.toLocaleLowerCase()}
                </label>
                <div className="flex flex-col gap-3 md:gap-4">
                  <SelectRadio
                    selected={watchKnowsDate === 'true'}
                    id="yes"
                    value="true"
                    title={
                      <span
                        className={clsx({
                          '!font-medium text-sm md:text-lg text-mobile-gray-400 leading-[140%]':
                            true,
                          'text-mobile-text-main': watchKnowsDate === 'true',
                        })}
                      >
                        Yes, I know the exact{' '}
                        {formSchema?.text?.toLocaleLowerCase()}
                      </span>
                    }
                    {...register('knowsDate', {
                      required: true,
                    })}
                  />
                  <SelectRadio
                    selected={watchKnowsDate === 'false'}
                    id="no"
                    value="false"
                    title={
                      <span
                        className={clsx({
                          '!font-medium text-sm md:text-lg text-mobile-gray-400 leading-[140%]':
                            true,
                          'text-mobile-text-main': watchKnowsDate === 'false',
                        })}
                      >
                        No, I haven't finalised it yet
                      </span>
                    }
                    {...register('knowsDate', {
                      required: true,
                    })}
                  />
                </div>
              </div>
            )}
            {watchKnowsDate || noTentativeDatesPresent ? (
              <div>
                <div>
                  {watchKnowsDate === 'true' || noTentativeDatesPresent ? (
                    <DatePicker
                      inputProps={{
                        label:
                          watchKnowsDate === 'true' || noTentativeDatesPresent
                            ? formSchema?.text
                            : `Select a tentative ${formSchema?.text?.toLocaleLowerCase()}`,
                        inputProps: {
                          className:
                            'bg-white my-2 block px-0 md:px-4 !rounded-none md:rounded-lg border-b md:border border-mobile-gray-100 h-12 outline-none focus:border-b-mobile-gray-500 md:focus:border-mobile-gray-500 cursor-pointer placeholder:text-gray-400 placeholder:text-sm placeholder:font-medium',
                          containerClassName: 'border-none lg:w-1/2 w-full',
                          required:
                            watchKnowsDate === 'true' ||
                            noTentativeDatesPresent,
                        },
                      }}
                      placeholder={`Pick a ${formSchema?.text?.toLocaleLowerCase()}`}
                      value={dateInputValue}
                      popperOptions={{
                        placement: noTentativeDatesPresent
                          ? 'auto'
                          : (viewportWidth ?? 0) < 1024
                            ? 'top-end'
                            : 'top',
                        strategy: 'fixed',
                      }}
                      // @ts-ignore
                      dayPickerProps={{
                        required:
                          watchKnowsDate === 'true' || noTentativeDatesPresent,
                        selected: new Date(watchDate ?? ''),
                        onSelect: (selectedDate: Date) => {
                          setValue('date', selectedDate?.toISOString());
                        },
                      }}
                    />
                  ) : (
                    // <input
                    //   placeholder={`Pick a ${formSchema?.text?.toLocaleLowerCase()}`}
                    //   className="bg-white mt-4 block lg:w-[50%] w-full p-2 rounded-lg border border-mobile-gray-100 h-12"
                    //   type="date"
                    //   {...register('date', {
                    //     required:
                    //       watchKnowsDate === 'true' || noTentativeDatesPresent,
                    //   })}
                    //   value={dateInputValue}
                    //   defaultValue={dateInputValue}
                    //   // min={formSchema?.lower_limit}
                    //   // max={formSchema?.upper_limit}
                    // />
                    <div
                      className={clsx({
                        'flex mt-4 no-scrollbar items-center gap-4 w-full overflow-scroll flex-shrink-0':
                          true,
                        'w-screen -ml-[20px] px-5 !max-w-[100vw] ': isMobile,
                      })}
                    >
                      {tentativeDates?.map(({ value, text }) => {
                        return (
                          <RadioTab
                            checked={Number(watchTentativeDate) === value}
                            key={value}
                            value={value}
                            title={
                              <span className="flex-shrink-0">{text}</span>
                            }
                            required={
                              watchKnowsDate === 'false' &&
                              !noTentativeDatesPresent
                            }
                            id={String(value)}
                            {...register('tentativeDays', {
                              onChange: () => {
                                setValue('date', new Date().toISOString());
                              },
                            })}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
                {watchKnowsDate && watchKnowsDate !== 'false' && formSchema ? (
                  <span className="text-mobile-gray-400 text-sm leading-[160%]">
                    Policy range:{' '}
                    {format(new Date(formSchema.lower_limit), 'dd MMM yyyy')} -{' '}
                    {format(new Date(formSchema.upper_limit), 'dd MMM yyyy')}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </fieldset>
        <Layout.Footer isSticky containerClassName="flex justify-between">
          <Stepper
            className="hidden md:flex"
            activeStep={3}
            activeSubStep={0}
            totalSubSteps={0}
            totalSteps={4}
            title="Tell us a bit more"
            subTitle="Step 3/4"
          />
          <Button
            loading={isFetching}
            type="submit"
            className="ml-auto md:max-w-[260px] w-full text-base"
          >
            Continue
          </Button>
        </Layout.Footer>
      </form>
    </Layout.Main>
  );
};

export default BitMoreInformation;
